import moment, { Moment } from 'moment';
import { Entitlement } from 'chargebee-typescript/lib/resources';
import { SubscriptionItem } from 'chargebee-typescript/lib/resources/subscription';
import { BillingInfo, Charge, Plan } from '../schemas/bt/subscription';
import { Workspace } from '../schemas/bt/workspace';

// Use a generic Plan interface that works for both backend and frontend
// Make all Plan properties optional for flexibility
interface GenericPlan extends Partial<Plan> {
    // Additional properties that might not be in the main Plan interface
}

/** Days of dunning before the account is UI-blocked, by decline type. */
export const HARD_BLOCK_DAYS = 9;
export const SOFT_BLOCK_DAYS = 21;

/**
 * Gateway decline codes where the issuer is telling us this card will never work again.
 * Chargebee stops dunning at attempt 0 for these, so waiting recovers nothing — only a new
 * card does. Measured 2026-07-27: these dominate the invoices that exhaust after a single
 * attempt, and 77% of those accounts were already dormant before the card ever failed.
 */
const HARD_DECLINE_CODES = new Set([
    'restricted_card',
    'card_lost_or_stolen',
    'lost_card',
    'stolen_card',
    'account_closed',
    'invalid_card_number',
    'incorrect_number',
    'no_such_card',
    'pickup_card',
    'card_not_supported',
    'agreement_already_cancelled',
    'payer_cannot_pay',
]);

/**
 * Gateway decline codes that are transient — the card is fine, this particular charge failed.
 * Chargebee's retry ladder recovers these on the SAME card once funds/limits free up.
 *
 * This is an ALLOW-LIST on purpose. Only a code that appears here earns the longer grace, so an
 * unrecognised code can never silently relax the block. Note `card_declined` is deliberately
 * ABSENT: it is Chargebee's generic bucket (631 of 1,395 failures measured 2026-07-27) mapping a
 * mix of soft and hard issuer responses, so it keeps today's stricter day-9 block.
 */
const SOFT_DECLINE_CODES = new Set([
    'limit_exceeded',
    'do_not_honor',
    'transaction_not_authorized',
    'insufficient_funds',
    'processing_error',
    'transaction_refused',
    'instrument_declined',
    'try_again_later',
    'issuer_not_available',
    'approve_with_id',
]);

const normalize = (code?: string | null): string => String(code ?? '').trim().toLowerCase();

/** True when the decline means "never retry this card" — only a new card recovers it. */
export const isHardDecline = (code?: string | null): boolean =>
    !!code && HARD_DECLINE_CODES.has(normalize(code));

/** True when the decline is transient and Chargebee's retries can still win on the same card. */
export const isSoftDecline = (code?: string | null): boolean =>
    !!code && SOFT_DECLINE_CODES.has(normalize(code));

export class SubscriptionObject {
    id?: string;
    _id?: string;
    status?: string;
    origStatus?: string;
    referenceNumber?: string;
    plan_id?: string;
    plan?: GenericPlan | SubscriptionItem;
    start_time?: Date;
    type?: 'paypal' | 'hotmart' | 'bluesnap' | 'chargebee' | 'trial' | undefined;
    recurrency_period?: number;
    workspace?: Workspace;
    subscription_id?: string;
    createdAt?: string;
    updatedAt?: string;
    expiredDate?: string;
    next_billing_at?: number;
    billing_info?: BillingInfo;
    entitlementList?: Entitlement[];
    chargesList?: Charge[];
    due_since?: number;
    due_invoices_count?: number;
    /** Server-authoritative dunning-block flag, stamped by the /subscriptions find hook
     *  (computed with the server clock). When present it overrides the local computation. */
    accountBlocked?: boolean;
    /** Gateway error_code of the most recent failed payment, captured from the Chargebee
     *  `payment_failed` webhook. Drives the decline-type-aware block grace period. */
    lastDeclineCode?: string;

    constructor(data: any) {
        Object.assign(this, data);
        this.start_time = moment(this.start_time).toDate();
    }

    getType(): 'paypal' | 'hotmart' | 'bluesnap' | 'chargebee' | 'trial' | undefined {
        // Use the type field if available, otherwise infer from plan_id
        if (this.type) {
            return this.type;
        }
        
        // If plan_id starts with "P-", it's a PayPal subscription
        if (this.plan_id?.startsWith('P-')) {
            return 'paypal';
        }
        
        return undefined;
    }

    /**
     * FE#241 — the authoritative tier for a Chargebee subscription, taken from the plan_id
     * (e.g. "Pro-USD-Monthly" → "Pro"). The plan_id is the same source `order`, `messageLimit`
     * and `planType` already trust; the entitlementList is a LIVE server-side Chargebee
     * enrichment that is never persisted, so when it fails to reach the client the tier
     * discriminators (hasSocket/hasBulk/hasTasks) must fall back to the plan_id rather than
     * collapse a paying Pro/Standard customer to Basic.
     */
    private chargebeePlanTier(): 'Basic' | 'Standard' | 'StandardNew' | 'Pro' | undefined {
        const [tier] = (this.plan_id ?? '').split('-');
        if (tier === 'Basic' || tier === 'Standard' || tier === 'StandardNew' || tier === 'Pro') {
            return tier;
        }
        return undefined;
    }

    getPlanFromMap(): GenericPlan | undefined {
        // Return this.plan if it's a GenericPlan (has intervalCount and intervalUnit properties)
        if (this.plan && 'intervalCount' in this.plan && 'intervalUnit' in this.plan) {
            return this.plan as GenericPlan;
        }
        return undefined;
    }

    get renewalTime() {
        let renewalTime: Moment | undefined;
        switch (this.getType()) {
            case undefined:
            case 'paypal': {
                const plan = this.getPlanFromMap();
                if (plan) {
                    // For PayPal subscriptions, use billing_info.last_payment.time if available, otherwise fall back to start_time
                    const baseTime = this.billing_info?.last_payment?.time || this.start_time;
                    renewalTime = moment.utc(baseTime).add((plan as GenericPlan)?.intervalCount, (plan as GenericPlan)?.intervalUnit);
                }
                break;
            }
            case 'hotmart':
            case 'bluesnap': {
                const plan = this.getPlanFromMap();
                if (plan) {
                    renewalTime = moment.utc(this.start_time).add((plan as GenericPlan)?.intervalCount, (plan as GenericPlan)?.intervalUnit);
                }
                break;
            }
            case 'chargebee': {
                renewalTime = moment.utc((this.next_billing_at ?? 0) * 1000)
                break;
            }
        }

        return renewalTime;
    }

    get isActive() {
        let isActive: boolean = false;
        switch (this.getType()) {
            case undefined:
            case 'paypal': {
                isActive = (this.status === 'ACTIVE' && this.renewalTime?.isAfter(moment())) ?? false;
                break;
            }
            case 'bluesnap':
            case 'hotmart': {
                isActive = (this.status?.toUpperCase() === 'ACTIVE' || this.renewalTime?.isAfter(moment())) ?? false;
                break;
            }
            case 'chargebee':
                // in_trial: a no-card Pro trial is entitled as its PLAN (Pro trial = Pro)
                // until Chargebee's trial-end scheduler flips it to cancelled
                // (see backend start-pro-trial.ts). Widen active to include it.
                isActive = ['active', 'non_renewing', 'in_trial'].indexOf(this.status ?? '') > -1;
                break;
            case 'trial': {
                isActive = moment(this.expiredDate).isAfter(moment());
                break;
            }
        }
        return isActive;
    }

    get isLive() {
        let isActive: boolean = false;
        switch (this.getType()) {
            case undefined:
            case 'paypal': {
                isActive = this.origStatus?.toLowerCase() === 'active';
                break;
            }
            case 'bluesnap':
            case 'hotmart': {
                isActive = this.origStatus?.toLowerCase() === 'active';
                break;
            }
            case 'chargebee':{
                isActive = this.status === 'active';
                break;
            }
        }
        return isActive;
    }

    get planId() {
        let planId: string = '';
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                planId = this.plan_id ?? '';
                break;
            }
        }
        return planId;
    }

    get planName() {
        let planName: string = '';
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                planName = (this.plan as GenericPlan)?.planName ?? '';
                break;
            }
            case 'chargebee': {
                planName = this.plan_id ?? '';
                break;
            }
        }
        return planName;
    }

    get planType() {
        let planType: string = '';
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                planType = (this.plan as GenericPlan)?.planType ?? '';
                break;
            }
            case 'chargebee': {
                [planType] = (this.plan_id ?? '').split('-');
                break;
            }
        }
        return planType;
    }

    get order(): number {
        let order = 0;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                order = (this.plan as GenericPlan)?.order ?? 0;
                break;
            }
            case 'chargebee': {
                const [plan_id] = (this.plan_id as string).split('-');
                switch (plan_id) {
                    case 'Basic': {
                        order = 1;
                        break;
                    }
                    case 'Standard':
                    case 'StandardNew': {
                        order = 2;
                        break;
                    }
                    case 'Pro': {
                        order = 3;
                        break;
                    }
                }
                break;
            }
        }

        return order;
    }

    get seatCount() {
        let seatCount: number = 0;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                seatCount = (this.plan as GenericPlan)?.seatCount ?? 0;
                break;
            }
            case 'chargebee': {
                seatCount = (this.plan as SubscriptionItem)?.quantity ?? 0;
                break;
            }
        }
        return seatCount;
    }

    get messageLimit() {
        let messageLimit: number = 0;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                messageLimit = (this.plan as GenericPlan)?.messageLimit ?? 1;
                break;
            }
            case 'chargebee': {
                messageLimit = (this.plan_id?.includes('Standard') || this.plan_id?.includes('Pro') || (this.plan_id?.includes('Basic'))) ? -1 : 1;
                break;
            }
        }
        return messageLimit === -1 ? Infinity : messageLimit;
    }

    get hasBulk() {
        let hasBulk: boolean = false;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                const plan = this.getPlanFromMap();
                hasBulk = (plan as GenericPlan)?.bulk ?? false;
                break;
            }
            case 'chargebee': {
                // Bulk/remove-branding is a Standard+ feature. Fall back to the plan_id tier
                // (FE#241) when the live entitlementList did not reach the client.
                const tier = this.chargebeePlanTier();
                hasBulk = (this.entitlementList ?? []).some(entitlement => entitlement.feature_id === 'remove-branding')
                    || tier === 'Standard' || tier === 'StandardNew' || tier === 'Pro';
                break;
            }
        }
        return hasBulk;
    }

    hasTasks(): boolean {
        let hasTasks: boolean = false;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                const plan = this.getPlanFromMap();
                hasTasks = (plan as GenericPlan)?.tasks ?? false;
                break;
            }
            case 'chargebee': {
                // Unlimited tasks is a Standard+ feature. Fall back to the plan_id tier
                // (FE#241) when the live entitlementList did not reach the client.
                const tier = this.chargebeePlanTier();
                hasTasks = (this.entitlementList ?? []).some(entitlement => entitlement.feature_id === 'unlimited-tasks')
                    || tier === 'Standard' || tier === 'StandardNew' || tier === 'Pro';
                break;
            }
        }

        return hasTasks;
    }

    hasSocket() {
        let hasSocket: boolean = false;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                const plan = this.getPlanFromMap();
                hasSocket = (plan as GenericPlan)?.bulk ?? false;
                break;
            }
            case 'chargebee': {
                // The offline socket is the Pro-only discriminator. Fall back to the plan_id
                // tier (FE#241) when the live entitlementList did not reach the client, so an
                // active Pro sub is not misread as Basic.
                hasSocket = (this.entitlementList ?? []).some(entitlement => entitlement.feature_id === 'offline-socket')
                    || this.chargebeePlanTier() === 'Pro';
                break;
            }
        }
        return hasSocket;
    }

    /**
     * Check if the user has a Pro account subscription
     * For Chargebee subscriptions: checks for 'offline-socket' entitlement
     * For older subscription types (paypal, hotmart, bluesnap): checks if plan has 'bulk' property
     * This is used to enforce subscription requirements for active gateways (engines)
     */
    hasProAccount(): boolean {
        let hasProAccount: boolean = false;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                const plan = this.getPlanFromMap();
                hasProAccount = (plan as GenericPlan)?.bulk ?? false;
                break;
            }
            case 'chargebee': {
                // Pro is defined by the offline socket; fall back to the plan_id tier (FE#241)
                // when the live entitlementList did not reach the client.
                hasProAccount = (this.entitlementList ?? []).some(entitlement => entitlement.feature_id === 'offline-socket')
                    || this.chargebeePlanTier() === 'Pro';
                break;
            }
        }
        return hasProAccount;
    }

    /**
     * Whether the workspace carries the Unlimited-Auto-Replies add-on ($25/mo).
     * `unlimited_auto_replies` is a Chargebee SWITCH feature (value "true") delivered on the
     * live entitlementList — NOT a plan-tier discriminator, so (unlike hasSocket/hasBulk/
     * hasProAccount) there is no plan_id fallback: the add-on can sit on any tier. Presence of
     * the entitlement counts as granted unless it is explicitly disabled (`is_enabled === false`).
     * Used to run auto-reply automation sessions UN-metered (skip the AUTOMATION_SESSION debit).
     */
    hasUnlimitedAutoReplies(): boolean {
        return (this.entitlementList ?? []).some(
            entitlement => entitlement.feature_id === 'unlimited_auto_replies'
                && (entitlement as { is_enabled?: boolean }).is_enabled !== false,
        );
    }

    /**
     * Canonical "is this the Pro tier?" check — the single source of truth for
     * Pro-gated features (e.g. AI-assisted assistant setup).
     *
     * Mirrors the plan-tier determination the SubscriptionStatusBar renders from:
     * Pro = an ACTIVE subscription with the offline socket entitlement AND
     * unlimited messages (the two features that distinguish Pro from Standard,
     * which also has unlimited messages but no offline socket). Built on the
     * existing entitlement methods so callers don't re-derive the rule.
     */
    isPro(): boolean {
        return this.isActive && this.hasSocket() && this.messageLimit === Infinity;
    }

    /**
     * Get the allowed number of engines for this subscription
     * Currently returns 1 if the user has offline-socket entitlement, 0 otherwise
     * This can be extended later to support different engine counts per plan
     */
    getAllowedEngineCount(): number {
        return this.hasSocket() ? 1 : 0;
    }

    /**
     * Whether this subscription grants programmatic API access (e.g. MCP tools).
     * Today: any active subscription qualifies, regardless of tier. When pricing
     * gates a dedicated entitlement (e.g. `mcp-api-access`) we'll check it here.
     */
    hasAPIAccess(): boolean {
        return this.isActive;
    }

    /**
     * Get the limit for custom contact labels (CRM feature)
     * Returns: number (3, 5, etc.) or Infinity for unlimited
     * Free: 3, Basic: 5, Standard/Pro: Unlimited
     */
    getCrmLabelsLimit(): number {
        const defaultFreeLimit = 3;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                // For older subscription types, return default free limit
                return defaultFreeLimit;
            }
            case 'chargebee': {
                const entitlement = (this.entitlementList ?? []).find(e => e.feature_id === 'crm-labels');
                if (!entitlement) {
                    return defaultFreeLimit;
                }
                if (entitlement.value === 'Unlimited' || entitlement.value === 'unlimited') {
                    return Infinity;
                }
                return parseInt(entitlement.value ?? '', 10) || defaultFreeLimit;
            }
            case 'trial': {
                // Trial users get unlimited CRM features
                return Infinity;
            }
        }
        return defaultFreeLimit;
    }

    /**
     * Get the limit for notes per contact (CRM feature)
     * Returns: number (5, 10, etc.) or Infinity for unlimited
     * Free: 5, Basic: 10, Standard/Pro: Unlimited
     */
    getCrmNotesPerContactLimit(): number {
        const defaultFreeLimit = 5;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                return defaultFreeLimit;
            }
            case 'chargebee': {
                const entitlement = (this.entitlementList ?? []).find(e => e.feature_id === 'crm-notes-per-contact');
                if (!entitlement) {
                    return defaultFreeLimit;
                }
                if (entitlement.value === 'Unlimited' || entitlement.value === 'unlimited') {
                    return Infinity;
                }
                return parseInt(entitlement.value ?? '', 10) || defaultFreeLimit;
            }
            case 'trial': {
                return Infinity;
            }
        }
        return defaultFreeLimit;
    }

    /**
     * Get the total limit for notes across all contacts (CRM feature)
     * Returns: number (50, 100, etc.) or Infinity for unlimited
     * Free: 50, Basic: 100, Standard/Pro: Unlimited
     */
    getCrmNotesTotalLimit(): number {
        const defaultFreeLimit = 50;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                return defaultFreeLimit;
            }
            case 'chargebee': {
                const entitlement = (this.entitlementList ?? []).find(e => e.feature_id === 'crm-notes-total');
                if (!entitlement) {
                    return defaultFreeLimit;
                }
                if (entitlement.value === 'Unlimited' || entitlement.value === 'unlimited') {
                    return Infinity;
                }
                return parseInt(entitlement.value ?? '', 10) || defaultFreeLimit;
            }
            case 'trial': {
                return Infinity;
            }
        }
        return defaultFreeLimit;
    }

    /**
     * Get the limit for active reminders (CRM feature)
     * Returns: number (10, 50, etc.) or Infinity for unlimited
     * Free: 10, Basic: 50, Standard/Pro: Unlimited
     */
    getCrmRemindersLimit(): number {
        const defaultFreeLimit = 10;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                return defaultFreeLimit;
            }
            case 'chargebee': {
                const entitlement = (this.entitlementList ?? []).find(e => e.feature_id === 'crm-reminders');
                if (!entitlement) {
                    return defaultFreeLimit;
                }
                if (entitlement.value === 'Unlimited' || entitlement.value === 'unlimited') {
                    return Infinity;
                }
                return parseInt(entitlement.value ?? '', 10) || defaultFreeLimit;
            }
            case 'trial': {
                return Infinity;
            }
        }
        return defaultFreeLimit;
    }

    get price(): number {
        const genericPlanPrice = (this.plan as GenericPlan)?.price;
        const subscriptionItemPrice = ((this.plan as SubscriptionItem)?.amount ?? 0) / 100;
        
        return genericPlanPrice ?? subscriptionItemPrice;
    }

    get isWorkspaceSubscription() {
        return false;
    }

    get billingPeriod(): 'month' | 'year' {
        let billingPeriod: 'month' | 'year' = 'month';
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                const plan = this.getPlanFromMap();
                billingPeriod = (plan as GenericPlan)?.intervalUnit ?? 'month';
                break;
            }
            case 'chargebee': {
                const [plan_id, currency, interval] = (this.plan_id as string).split('-');
                billingPeriod = interval === 'Monthly' ? 'month' : 'year';
                break;
            }
        }
        return billingPeriod;
    }

    get amount() {
        let amount;
        switch (this.getType()) {
            case undefined:
            case 'paypal':
            case 'hotmart':
            case 'bluesnap': {
                const plan = this.getPlanFromMap();
                amount = (plan as GenericPlan)?.price ?? 0;
                break
            }
            case 'chargebee': {
                amount = ((this.plan as SubscriptionItem)?.amount ?? 0) / 100;
                break;
            }
            case 'trial': {
                amount = 0;
                break;
            }
        }

        return amount;
    }

    get isDunning(): boolean {
        return this.type === 'chargebee' && this.due_invoices_count != null && this.due_invoices_count > 0;
    }

    get dunningDays(): number {
        if (!this.isDunning || !this.due_since) return 0;
        return Math.floor(moment().diff(moment.utc(this.due_since * 1000), 'days', true));
    }

    /**
     * Days of dunning we allow before blocking, chosen by decline type.
     *
     * A SOFT decline (insufficient funds, limit exceeded, do-not-honor) is usually recovered by
     * Chargebee retrying the SAME card once the balance replenishes — measured 2026-07-27 over 90
     * days, 48% of all successful recoveries landed after day 9 and 88% of those needed no customer
     * action at all. Blocking those at day 9 withholds the product from customers who were going to
     * pay regardless, so they get until day 21 (past the p75 of the recovery curve).
     *
     * A HARD decline keeps the day-9 block: retries provably never recover it, so the block is the
     * only thing that can prompt the new card it actually needs.
     *
     * An unknown, absent or ambiguous code (e.g. the generic `card_declined`) keeps day 9 —
     * today's behaviour — so this can only ever relax the block on a positively-identified soft
     * decline. Soft is an allow-list, not the complement of hard.
     */
    get blockAfterDays(): number {
        return isSoftDecline(this.lastDeclineCode) ? SOFT_BLOCK_DAYS : HARD_BLOCK_DAYS;
    }

    get isAccountBlocked(): boolean {
        // Server-authoritative: the /subscriptions find hook stamps `accountBlocked` computed with
        // the server clock, so the UI block can't be bypassed by tampering with the client clock.
        // Fall back to local computation when unstamped (e.g. the backend itself before stamping).
        if (this.accountBlocked != null) return this.accountBlocked;
        return this.isDunning && this.dunningDays > this.blockAfterDays;
    }
}
